import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

// Global indeterminate top progress bar. Driven by the apiClient interceptors
// in App.js, which only emit an "api-progress" event for requests that run
// longer than a short threshold — so fast (cached) calls never flash it, but
// slow first-time report/drill computes get clear visual feedback.
//
// If a slow load runs past LABEL_MS, we also surface a small centred banner
// explaining this is a one-time first-run compute, so the wait feels expected.
const LABEL_MS = 3000;

export const TopProgressBar = () => {
  const [active, setActive] = useState(0);
  const [showLabel, setShowLabel] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => setActive(e.detail || 0);
    window.addEventListener('api-progress', handler);
    return () => window.removeEventListener('api-progress', handler);
  }, []);

  useEffect(() => {
    if (active > 0) {
      if (!timerRef.current) {
        timerRef.current = setTimeout(() => setShowLabel(true), LABEL_MS);
      }
    } else {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      setShowLabel(false);
    }
  }, [active]);

  if (active <= 0) return null;

  return (
    <>
      <div
        data-testid="global-progress-bar"
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: '3px',
          zIndex: 99999, overflow: 'hidden', background: 'rgba(124,58,237,0.14)',
        }}
      >
        <style>{`@keyframes gpbSlide{0%{left:-45%}100%{left:100%}}@keyframes gpbFade{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>
        <div
          style={{
            position: 'absolute', top: 0, height: '100%', width: '45%', left: '-45%',
            background: 'linear-gradient(90deg, transparent, #7c3aed, #a855f7, transparent)',
            animation: 'gpbSlide 1.05s ease-in-out infinite',
          }}
        />
      </div>

      {showLabel && (
        <div
          style={{
            position: 'fixed', top: '12px', left: 0, right: 0, zIndex: 99999,
            display: 'flex', justifyContent: 'center', pointerEvents: 'none',
          }}
        >
          <div
            data-testid="global-progress-label"
            style={{
              pointerEvents: 'auto',
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              maxWidth: '92vw',
              padding: '8px 14px', borderRadius: '9999px',
              background: 'rgba(30,27,55,0.94)', color: '#ede9fe',
              fontSize: '12.5px', fontWeight: 500, lineHeight: 1.3,
              boxShadow: '0 6px 22px rgba(76,29,149,0.35)',
              border: '1px solid rgba(167,139,250,0.35)',
              backdropFilter: 'blur(6px)',
              animation: 'gpbFade 0.25s ease-out',
            }}
          >
            <Loader2 size={14} className="animate-spin" style={{ color: '#a855f7', flexShrink: 0 }} />
            <span>Preparing this report for the first time — this one-time compute may take a few seconds. Later opens are instant.</span>
          </div>
        </div>
      )}
    </>
  );
};
